import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Tutor, TutorGender, TutorStatus } from '@/types';
import { tutorApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { ImageUploadField } from '@/components/forms/ImageUploadField';
import { getTutorAvatarUrl } from '@/utils/tutorAvatar';
import { useAuthStore } from '@/store/authStore';

type TutorFormData = {
  name: string;
  gender: TutorGender;
  title: string;
  yearsExperience?: number;
  bio: string;
  expertiseCsv: string;
  profileImageKey: string;
  linkedinUrl: string;
  websiteUrl: string;
};

const defaultValues: TutorFormData = {
  name: '',
  gender: 'PREFER_NOT_TO_SAY',
  title: '',
  yearsExperience: 0,
  bio: '',
  expertiseCsv: '',
  profileImageKey: '',
  linkedinUrl: '',
  websiteUrl: '',
};

const statusClasses: Record<TutorStatus, string> = {
  Pending: 'border-amber-200 bg-amber-50 text-amber-800',
  Approved: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  Rejected: 'border-rose-200 bg-rose-50 text-rose-800',
};

function toFormValues(tutor: Tutor): TutorFormData {
  return {
    name: tutor.name || '',
    gender: tutor.gender,
    title: tutor.title || '',
    yearsExperience: tutor.yearsExperience ?? 0,
    bio: tutor.bio || '',
    expertiseCsv: (tutor.expertise || []).join(', '),
    profileImageKey: tutor.profileImageKey || '',
    linkedinUrl: tutor.linkedinUrl || '',
    websiteUrl: tutor.websiteUrl || '',
  };
}

function getStatusMessage(status: TutorStatus) {
  if (status === 'Approved') {
    return 'This tutor profile is approved and available throughout the application.';
  }

  if (status === 'Rejected') {
    return 'This tutor profile is rejected and hidden from public use until an admin updates its status.';
  }

  return 'This tutor profile is waiting for admin approval and is not yet visible in courses or public pages.';
}

function TutorStatusBadge({ status }: { status: TutorStatus }) {
  return <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusClasses[status]}`}>{status}</span>;
}

export const AdminTutorForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';
  const cancelPath = isAdmin ? '/admin/tutors' : '/tutor/courses';
  const isEditMode = Boolean(isAdmin && id);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [submitError, setSubmitError] = useState('');
  const [fetchError, setFetchError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [linkedAccountEmail, setLinkedAccountEmail] = useState('');
  const [existingTutors, setExistingTutors] = useState<Tutor[]>([]);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TutorFormData>({ defaultValues });

  useEffect(() => {
    const controller = new AbortController();

    const fetchTutor = async () => {
      try {
        setFetchError('');
        setSubmitSuccess('');

        if (isAdmin) {
          if (!isEditMode || !id) {
            setExistingTutors([]);
            setLinkedAccountEmail('');
            reset(defaultValues);
            setProfilePreviewUrl('');
            return;
          }

          const tutor = await tutorApi.getTutorById(id, {
            signal: controller.signal,
            scope: 'management',
          });

          if (!tutor) {
            setFetchError('Tutor profile not found.');
            return;
          }

          setExistingTutors([tutor]);
          setLinkedAccountEmail(tutor.linkedAccountEmail || '');
          reset(toFormValues(tutor));
          setProfilePreviewUrl(tutor.profilePhotoUrl || '');
          return;
        }

        const tutors = await tutorApi.getMyTutorProfiles(controller.signal);
        setExistingTutors(tutors);
        setLinkedAccountEmail(tutors[0]?.linkedAccountEmail || '');
        reset(defaultValues);
        setProfilePreviewUrl('');
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          setFetchError((error as Error).message || 'Failed to load tutor profile.');
        }
      } finally {
        setIsFetching(false);
      }
    };

    void fetchTutor();
    return () => controller.abort();
  }, [id, isAdmin, isEditMode, reset]);

  const watched = watch();

  const previewTutor: Tutor = useMemo(
    () => ({
      id: 'preview',
      status: 'Pending',
      name: watched.name || 'Tutor Name',
      gender: watched.gender || 'PREFER_NOT_TO_SAY',
      title: watched.title || '',
      yearsExperience: watched.yearsExperience ?? 0,
      bio: watched.bio || '',
      expertise: (watched.expertiseCsv || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      profileImageKey: watched.profileImageKey || '',
      profilePhotoUrl: profilePreviewUrl || '',
      linkedinUrl: watched.linkedinUrl || '',
      websiteUrl: watched.websiteUrl || '',
    }),
    [
      watched.bio,
      watched.expertiseCsv,
      watched.gender,
      watched.linkedinUrl,
      watched.name,
      watched.profileImageKey,
      watched.title,
      watched.websiteUrl,
      watched.yearsExperience,
      profilePreviewUrl,
    ]
  );

  const onSubmit = async (data: TutorFormData) => {
    setSubmitError('');
    setSubmitSuccess('');
    setIsLoading(true);

    const payload: Partial<Tutor> = {
      name: data.name.trim(),
      gender: data.gender,
      title: data.title.trim(),
      yearsExperience: data.yearsExperience ?? 0,
      bio: data.bio.trim(),
      expertise: (data.expertiseCsv || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      profileImageKey: data.profileImageKey.trim(),
      linkedinUrl: data.linkedinUrl.trim(),
      websiteUrl: data.websiteUrl.trim(),
    };

    try {
      const savedTutor =
        isAdmin && isEditMode && id
          ? await tutorApi.updateTutor(id, payload)
          : await tutorApi.createTutor(payload);

      if (isAdmin) {
        navigate('/admin/tutors');
        return;
      }

      setExistingTutors((prev) => [...prev, savedTutor]);
      setSubmitSuccess('Your tutor profile has been submitted for admin approval.');
      reset(defaultValues);
      setProfilePreviewUrl('');
    } catch (error) {
      setSubmitError((error as Error).message || 'Failed to save tutor profile.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <Card className="max-w-3xl p-8">
        <p className="text-sm text-gray-600">Loading tutor profile...</p>
      </Card>
    );
  }

  if (fetchError) {
    return (
      <Card className="max-w-3xl p-8">
        <h1 className="text-2xl font-semibold text-gray-900">Unable to load tutor profile</h1>
        <p className="mt-2 text-sm text-red-700">{fetchError}</p>
        <div className="mt-6">
          <Button variant="primary" onClick={() => navigate(cancelPath)}>
            {isAdmin ? 'Back to Tutors' : 'Back to Tutor Dashboard'}
          </Button>
        </div>
      </Card>
    );
  }

  const hasExistingTutors = !isAdmin && existingTutors.length > 0;

  const activeTutor = previewTutor;

  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <h1 className="page-heading">
          {isAdmin
            ? isEditMode
              ? 'Edit Tutor Profile'
              : 'Create Tutor Profile'
            : hasExistingTutors
            ? 'My Tutor Profiles'
            : 'Create Tutor Profile'}
        </h1>
        <p className="page-subtitle">
          {isAdmin
            ? linkedAccountEmail
              ? `This profile is linked to a staff account (${linkedAccountEmail}).`
              : 'Admin-created tutor profiles are approved immediately and can be used across the application.'
            : hasExistingTutors
            ? 'You can submit additional tutor profiles for admin approval below.'
            : 'Your profile will be submitted for admin approval before it appears in courses or public pages.'}
        </p>
      </div>

      {hasExistingTutors ? (
        <div className="space-y-4 mb-8">
          {existingTutors.map((tutor) => (
            <Card key={tutor.id} className="p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">{tutor.name}</h2>
                  {tutor.title ? <p className="text-sm text-gray-600">{tutor.title}</p> : null}
                  <p className="mt-2 text-sm text-gray-500">Status: {tutor.status}</p>
                </div>
                <div className="text-sm text-gray-600">
                  <p>{getStatusMessage(tutor.status)}</p>
                  {tutor.createdAt ? <p className="mt-1 text-xs text-gray-500">Created {new Date(tutor.createdAt).toLocaleDateString()}</p> : null}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : null}

      {isAdmin && existingTutors[0] ? (
        <Card className="mb-6 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Current Status</p>
              <div className="mt-2">
                <TutorStatusBadge status={existingTutors[0].status} />
              </div>
            </div>
            <div className="text-sm text-gray-600">
              <p>{getStatusMessage(existingTutors[0].status)}</p>
              {existingTutors[0].createdBy?.email ? (
                <p className="mt-1 text-xs text-gray-500">
                  Created by {existingTutors[0].createdBy.email}
                  {existingTutors[0].createdBy.role ? ` (${existingTutors[0].createdBy.role})` : ''}
                </p>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <Card className="p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Profile</h2>

          <div className="mb-6 flex items-start gap-5">
            <img
              src={getTutorAvatarUrl(activeTutor)}
              alt={activeTutor.name}
              className="h-16 w-16 rounded-lg border border-slate-200 bg-slate-100 object-cover"
            />
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                If no profile image is uploaded, the platform will use a generated avatar.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label="Full Name"
              placeholder="e.g., Ananya Iyer"
              {...register('name', { required: 'Name is required' })}
              error={errors.name?.message}
            />

            <div>
              <label className="form-label">Gender</label>
              <select className="form-control min-h-11" {...register('gender')}>
                <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="NON_BINARY">Non-binary</option>
              </select>
            </div>

            <Input label="Professional Title" placeholder="e.g., Senior Football Coach" {...register('title')} />

            <Input
              label="Experience (years)"
              type="number"
              min={0}
              step={1}
              {...register('yearsExperience', {
                valueAsNumber: true,
                min: { value: 0, message: 'Experience cannot be negative' },
              })}
              error={errors.yearsExperience?.message}
            />
          </div>

          <div className="mt-4">
            <label className="form-label">Bio</label>
            <textarea
              className="form-control min-h-[120px]"
              placeholder="A short professional summary of teaching and coaching experience..."
              {...register('bio')}
            />
          </div>

          <div className="mt-4">
            <Input
              label="Expertise (comma-separated)"
              placeholder="React, Sprinting, Strength Training"
              {...register('expertiseCsv')}
            />
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Links & Photo</h2>

          <div className="grid grid-cols-1 gap-4">
            <input type="hidden" {...register('profileImageKey')} />
            <ImageUploadField
              label="Profile Image"
              folder="profile-images"
              imageKey={watched.profileImageKey || ''}
              previewUrl={profilePreviewUrl}
              helperText="Store only the generated S3 object key for tutor profiles."
              onChange={({ key, url }) => {
                setValue('profileImageKey', key, { shouldDirty: true });
                setProfilePreviewUrl(url);
              }}
            />
            <Input label="LinkedIn URL" placeholder="https://linkedin.com/in/..." {...register('linkedinUrl')} />
            <Input label="Website URL" placeholder="https://..." {...register('websiteUrl')} />
          </div>
        </Card>

        {submitError ? <p className="status-message border-red-100 bg-red-50 text-red-700">{submitError}</p> : null}
        {submitSuccess ? <p className="status-message border-emerald-100 bg-emerald-50 text-emerald-700">{submitSuccess}</p> : null}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
          <Button type="button" variant="ghost" onClick={() => navigate(cancelPath)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : isAdmin ? (isEditMode ? 'Update Tutor' : 'Create Tutor') : 'Submit for Approval'}
          </Button>
        </div>
      </form>
    </div>
  );
};
